import { obtainSlot } from './slot';
import { optionNullableClassDecorator } from './utils';
import { inject, ref, watch, getCurrentInstance, onBeforeMount, onMounted, onBeforeUpdate, onUpdated, onBeforeUnmount, onUnmounted, onActivated, onDeactivated, onRenderTracked, onRenderTriggered, onErrorCaptured, onServerPrefetch, toRef, } from 'vue';
import { toComponentReverse } from './utils';
import { Base } from './class';
export const ComposableSymbol = Symbol('vue-facing-decorator-composable');
const lifecycleMap = {
    beforeMount: onBeforeMount,
    mounted: onMounted,
    beforeUpdate: onBeforeUpdate,
    updated: onUpdated,
    beforeUnmount: onBeforeUnmount,
    unmounted: onUnmounted,
    activated: onActivated,
    deactivated: onDeactivated,
    renderTracked: onRenderTracked,
    renderTriggered: onRenderTriggered,
    errorCaptured: onErrorCaptured,
    serverPrefetch: onServerPrefetch,
};
export const Composable = optionNullableClassDecorator((cons) => {
    if (!(cons.prototype instanceof Base)) {
        throw new Error(`@Composable class "${cons.name}" must extend from "Vue" (vue-facing-decorator Base) to access Vue instance context like this.$router.`);
    }
    cons[ComposableSymbol] = true;
});
function getDeepProp(obj, path) {
    return path.split('.').reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], obj);
}
export function isComposable(value) {
    return typeof value === 'function' && value[ComposableSymbol] === true;
}
export function instantiateComposable(Cls, props, ctx) {
    const protoChain = toComponentReverse(Cls.prototype);
    const allSlots = protoChain.map(p => obtainSlot(p));
    const sample = new Cls();
    const raw = {};
    const dataKeys = Object.keys(sample);
    dataKeys.forEach((key) => {
        raw[key] = ref(sample[key]);
    });
    const inst = Object.create(Cls.prototype);
    for (const key of dataKeys) {
        Object.defineProperty(inst, key, {
            get: () => raw[key].value,
            set: (v) => { raw[key].value = v; },
            enumerable: true,
        });
    }
    for (const slot of allSlots) {
        const propMap = slot.getMap('props');
        if (propMap) {
            for (const [key] of propMap) {
                if (key in props) {
                    raw[key] = toRef(props, key);
                }
            }
        }
    }
    let promises = null;
    for (const slot of allSlots) {
        const setupMap = slot.getMap('setup');
        for (const name of (setupMap === null || setupMap === void 0 ? void 0 : setupMap.keys()) || []) {
            let value = setupMap.get(name).setupFunction(props, ctx);
            if (isComposable(value)) {
                value = instantiateComposable(value, props, ctx);
            }
            if (value instanceof Promise) {
                promises !== null && promises !== void 0 ? promises : (promises = []);
                promises.push(value.then(v => { inst[name] = v; }));
            }
            else {
                inst[name] = value;
            }
        }
    }
    for (const slot of allSlots) {
        const injectMap = slot.getMap('inject');
        injectMap === null || injectMap === void 0 ? void 0 : injectMap.forEach((config, name) => {
            var _a;
            const key = (_a = config.from) !== null && _a !== void 0 ? _a : name;
            inst[name] = inject(key, config.default);
        });
    }
    for (const proto of protoChain) {
        for (const hook in lifecycleMap) {
            const fn = proto[hook];
            if (typeof fn === 'function') {
                lifecycleMap[hook](() => fn.call(inst));
            }
        }
        if (typeof proto.created === 'function') {
            proto.created.call(inst);
        }
    }
    for (const slot of allSlots) {
        const watchMap = slot.getMap('watch');
        watchMap === null || watchMap === void 0 ? void 0 : watchMap.forEach((watchConfigs, name) => {
            const configs = Array.isArray(watchConfigs) ? watchConfigs : [watchConfigs];
            configs.forEach(conf => {
                var _a;
                const handler = (_a = conf.handler) !== null && _a !== void 0 ? _a : inst[name];
                if (typeof handler !== 'function')
                    return;
                watch(() => { var _a; return (conf.key in raw ? (_a = raw[conf.key]) === null || _a === void 0 ? void 0 : _a.value : getDeepProp(inst, conf.key)); }, (...args) => handler.apply(inst, args), {
                    immediate: conf.immediate,
                    deep: conf.deep,
                });
            });
        });
    }
    if (promises) {
        return Promise.all(promises).then(() => inst);
    }
    const instance = getCurrentInstance();
    if (instance) {
        const proxy = instance.proxy;
        if (proxy) {
            for (const key of ['$router', '$route', '$el', '$emit', '$attrs', '$refs', '$slots', '$toast', '$t']) {
                if (proxy[key] !== undefined) {
                    inst[key] = proxy[key];
                }
            }
            if (!('$t' in inst)) {
                Object.defineProperty(inst, '$t', {
                    get() {
                        return proxy.$t;
                    },
                    configurable: true,
                    enumerable: true,
                });
            }
        }
    }
    return inst;
}
//# sourceMappingURL=composable.js.map